import React from 'react'
import { iInterpreterPayloadResult } from 'store/types/interpreter'
import { saveAs } from 'file-saver'
import Papa from 'papaparse'
import ButtonWrapper from 'components/custom/ButtonWrapper'
import { eButtonTypes, eColor, eTypographyVariant } from 'components/types'
import TitleWrapper from 'components/custom/TitleWrapper'
import { convertInterpreterDataToCalculationProcessChain } from '../helpers'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { ModalNewName } from 'pages/Assembler/components/AssemblerModals'
import { sagaCreateAssemblyAction } from 'saga/actions/assembler'
import { Alert, Snackbar } from '@mui/material'
import { RootState } from 'store'
import { useSelector } from 'react-redux'

interface Props {
  data: iInterpreterPayloadResult[]
}

export const BOMInterpretedTable: React.FC<Props> = ({ data }) => {
  const [openModal, setOpenModal] = useState(false)
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false)
  const assemblyListing = useSelector(
    (state: RootState) => state.assemblyListing.assemblyListing?.data ?? []
  )
  const dispatch = useDispatch()

  if (!data || data.length === 0) return null

  const handleTransfer = (name: string) => {
    const processChain = convertInterpreterDataToCalculationProcessChain(data)
    dispatch(
      sagaCreateAssemblyAction({
        name: name,
        calculationProcessChain: processChain,
      })
    )
    setShowSuccessSnackbar(true)
  }

  const existingAssemblyNames = assemblyListing.map((asm) =>
    asm.name.toLowerCase()
  )

  const handleExportToCSV = () => {
    const csvData = data.map((item) => ({
      Process: item.calculationStepName,
      Sid: item.sid,
      Context: item.contextData.contextName,
      Entry: item.customName,
      SustamizeEntity: item.susName,
      DocumentationUrl: item.documentationUrl,
      PrimaryEmission: item.co2Base?.common ?? '',
      RecyclingEmission: item.co2Recycling?.common ?? '',
    }))

    const csv = Papa.unparse(csvData)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    saveAs(blob, 'interpreted_bom.csv')
  }

  return (
    <div className="mt-6">
      <div className="flex justify-between items-center mb-2">
        <TitleWrapper variant={eTypographyVariant.h4}>
          Interpreted BOM Table
        </TitleWrapper>

        {/* Group buttons together */}
        <div className="flex gap-2">
          <ButtonWrapper
            onClick={() => setOpenModal(true)}
            color={eColor.primary}
          >
            Transfer to Assembler
          </ButtonWrapper>
          <ButtonWrapper
            onClick={handleExportToCSV}
            color={eColor.primary}
            type={eButtonTypes.button}
          >
            Export to CSV
          </ButtonWrapper>
          <ModalNewName
            title="assembler.create"
            message="Create a New Assembly"
            open={openModal}
            handleClose={() => setOpenModal(false)}
            existingNames={existingAssemblyNames}
            yesOption={(name) => handleTransfer(name)}
          />
        </div>
      </div>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="border-b px-3 py-2 text-left bg-gray-100">
              Process
            </th>
            <th className="border-b px-3 py-2 text-left bg-gray-100">Entry</th>
            <th className="border-b px-3 py-2 text-left bg-gray-100">
              Sustamize Entity
            </th>
            <th className="border-b px-3 py-2 text-left bg-gray-100">sid</th>
            <th className="border-b px-3 py-2 text-left bg-gray-100">
              Context
            </th>
            <th className="border-b px-3 py-2 text-left bg-gray-100">
              Country/Region
            </th>
            <th className="border-b px-3 py-2 text-left bg-gray-100">
              Documentation
            </th>
            <th className="border-b px-3 py-2 text-left bg-gray-100">
              Primary Emission
            </th>
            <th className="border-b px-3 py-2 text-left bg-gray-100">
              Recycling Emission
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr key={idx}>
              <td className="border px-3 py-2">{row.calculationStepName}</td>
              <td className="border px-3 py-2">{row.customName}</td>
              <td className="border px-3 py-2">
                {row.susName && row.uuid && row.contextData?.contextName ? (
                  <a
                    href={`/entity/${row.uuid}?contextName=${row.contextData.contextName}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    {row.susName}
                  </a>
                ) : (
                  '-'
                )}
              </td>
              <td className="border px-3 py-2">{row.sid}</td>
              <td className="border px-3 py-2">
                {row.contextData.contextName}
              </td>
              <td className="border px-3 py-2">{row.params.country}</td>
              <td className="border px-3 py-2">
                {row.documentationUrl ? (
                  <a
                    href={row.documentationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    LINK
                  </a>
                ) : (
                  '-'
                )}
              </td>
              <td className="border px-3 py-2">{row.co2Base?.common ?? '-'}</td>
              <td className="border px-3 py-2">
                {row.co2Recycling?.common ?? '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessSnackbar(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Assembly saved successfully!
        </Alert>
      </Snackbar>
    </div>
  )
}
